import { useContext } from 'react';
import { Pagination } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import { Field, ImageField, Link, LinkField, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import { useRegisterReturnUrl } from 'src/hooks/useRegisterReturnUrl';

import { ComponentProps } from 'lib/ComponentProps';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { SwiperComponent, SwiperSlideComponent } from 'src/components/basicContent/_Swiper/Swiper';

import styles from '../GroupCallToAction/GroupCallToAction.module.css';

type AccessLevelGroupCallToActionProps = ComponentProps & {
	fields: {
		BenefitsLink: LinkField;
		EnableTransparentBackground: Field<boolean>;
		RegisteredImage: ImageField;
		RegisteredTitle: Field<string>;
		RegisteredDescription: Field<string>;
		RegisteredLink: LinkField;
		NonRegisteredImage: ImageField;
		NonRegisteredTitle: Field<string>;
		NonRegisteredDescription: Field<string>;
		NonRegisteredLink: LinkField;
		MemberImage: ImageField;
		MemberTitle: Field<string>;
		MemberDescription: Field<string>;
		MemberLink: LinkField;
		NonMemberImage: ImageField;
		NonMemberTitle: Field<string>;
		NonMemberDescription: Field<string>;
		NonMemberLink: LinkField;
		SubscriberImage: ImageField;
		SubscriberTitle: Field<string>;
		SubscriberDescription: Field<string>;
		SubscriberLink: LinkField;
		NonSubscriberImage: ImageField;
		NonSubscriberTitle: Field<string>;
		NonSubscriberDescription: Field<string>;
		NonSubscriberLink: LinkField;
		PaidImage: ImageField;
		PaidTitle: Field<string>;
		PaidDescription: Field<string>;
		PaidLink: LinkField;
	};
};

export default withDatasourceCheck()<AccessLevelGroupCallToActionProps>(AccessLevelGroupCallToAction);

function AccessLevelGroupCallToAction(props: AccessLevelGroupCallToActionProps): JSX.Element {
	const isEditing = IsSitecoreEditing();
	const RcnUser = useContext(RcnUserContext);

	const nonRegisteredLink = useRegisterReturnUrl(props.fields.NonRegisteredLink);

	return (
		<section className={`${styles.section} ${!props.fields.EnableTransparentBackground?.value ? styles.background : ''} cen padYL`}>
			<SwiperComponent
				modules={[Pagination]}
				pagination={{
					clickable: true,
				}}
				spaceBetween={15}
				slidesPerView={1.15}
				breakpoints={{
					600: {
						slidesPerView: 2,
					},
					800: {
						slidesPerView: 3,
					},
					1200: {
						slidesPerView: 4,
					},
				}}
				className={`${styles.slider} maxWidth`}
			>
				<>
					{(RcnUser.authenticatedUserProfile || isEditing) && (
						<SwiperSlide className={`${styles.slide} ${styles.highlighted} padX padYL rounded`}>
							<SwiperSlideComponent
								media={props.fields.RegisteredImage}
								title={props.fields.RegisteredTitle}
								description={props.fields.RegisteredDescription}
								link={props.fields.RegisteredLink}
							/>
						</SwiperSlide>
					)}

					{(!RcnUser.authenticatedUserProfile || isEditing) && (
						<SwiperSlide className={`${styles.slide} ${styles.background} padX padYL rounded`}>
							<SwiperSlideComponent
								media={props.fields.NonRegisteredImage}
								title={props.fields.NonRegisteredTitle}
								description={props.fields.NonRegisteredDescription}
								link={nonRegisteredLink}
								buttonColor="white"
							/>
						</SwiperSlide>
					)}

					{(!!RcnUser.authenticatedUserProfile?.isActiveMember || isEditing) && (
						<SwiperSlide className={`${styles.slide} ${styles.highlighted} padX padYL rounded`}>
							<SwiperSlideComponent
								media={props.fields.MemberImage}
								title={props.fields.MemberTitle}
								description={props.fields.MemberDescription}
								link={props.fields.MemberLink}
							/>
						</SwiperSlide>
					)}

					{(!RcnUser.authenticatedUserProfile?.isActiveMember || isEditing) && (
						<SwiperSlide className={`${styles.slide} ${styles.background} padX padYL rounded`}>
							<SwiperSlideComponent
								media={props.fields.NonMemberImage}
								title={props.fields.NonMemberTitle}
								description={props.fields.NonMemberDescription}
								link={props.fields.NonMemberLink}
								buttonColor="white"
							/>
						</SwiperSlide>
					)}

					{(RcnUser.authenticatedUserProfile?.isActiveRcniSubscriber || isEditing) && (
						<SwiperSlide className={`${styles.slide} ${styles.highlighted} padX padYL rounded`}>
							<SwiperSlideComponent
								media={props.fields.SubscriberImage}
								title={props.fields.SubscriberTitle}
								description={props.fields.SubscriberDescription}
								link={props.fields.SubscriberLink}
							/>
						</SwiperSlide>
					)}

					{(!RcnUser.authenticatedUserProfile?.isActiveRcniSubscriber || isEditing) && (
						<SwiperSlide className={`${styles.slide} ${styles.background} padX padYL rounded`}>
							<SwiperSlideComponent
								media={props.fields.NonSubscriberImage}
								title={props.fields.NonSubscriberTitle}
								description={props.fields.NonSubscriberDescription}
								link={props.fields.NonSubscriberLink}
								buttonColor="white"
							/>
						</SwiperSlide>
					)}

					<SwiperSlide className={`${styles.slide} ${styles.background} padX padYL rounded`}>
						<SwiperSlideComponent
							media={props.fields.PaidImage}
							title={props.fields.PaidTitle}
							description={props.fields.PaidDescription}
							link={props.fields.PaidLink}
							buttonColor="white"
						/>
					</SwiperSlide>
				</>
			</SwiperComponent>

			{(props.fields.BenefitsLink?.value?.href || isEditing) && (
				<Link field={props.fields.BenefitsLink} className={`${styles.sectionLink} link linkOffset linkHoverWeighted`} />
			)}
		</section>
	);
}
